import { useCallback, useMemo } from "react";

import { useUserContext } from "lib/contexts/UserProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { CheckoutErrorResponseCode } from "lib/types/checkout";
import { useLocale } from "./useLocale";
import { checkoutInfoToast } from "lib/utils/toasters";
import { CheckoutErrorResponse } from "lib/types/checkout";
import { UserType } from "lib/types/user";
import i18n from "assests/locales/i18n";
import { useOrderSummary } from "./useOrderSummary";
import { isEmptyObj } from "lib/utils/helpers";

export function useCheckoutErrors() {
  const { state } = useCheckoutContext();
  const { t } = useLocale();
  const {
    state: { user },
  } = useUserContext();

  const { unservicableItems, unservicableAddOnsCount } = useOrderSummary();

  const errorResponses = state.errorResponses;
  const shippingHandles = state.shippingHandles;
  const isCheckoutLoading = state.checkoutLoading;

  const errorFlags = useMemo(() => {
    return getErrorFlags(errorResponses);
  }, [errorResponses]);

  const {
    isAddItemsWorthDeliveryError,
    isWeightBasedShippingError,
    isSingleItemServicibilityError,
    isMultipleItemServicibilityError,
    isSelectedAddressNotServiceableError,
  } = errorFlags;

  const errorMessage = useMemo(() => {
    if (isCheckoutLoading || isEmptyObj(user)) {
      return "";
    }

    const _errorMessage = getServiceabilityErrorMessage(errorResponses, {
      user,
      count: unservicableItems.length + unservicableAddOnsCount,
    });
    if (_errorMessage) {
      return _errorMessage;
    }

    if (errorResponses.length === 0 && shippingHandles.length === 0) {
      return "";
    }

    return "";
  }, [errorResponses, shippingHandles, isCheckoutLoading, user, unservicableItems, unservicableAddOnsCount]);

  const triggerCheckoutErrorInfoToast = useCallback(
    (customErrorMessage?: string) => {
      if (customErrorMessage) {
        checkoutInfoToast(customErrorMessage);
      } else if (errorMessage) {
        checkoutInfoToast(errorMessage);
      }
    },
    [errorMessage],
  );

  return {
    errorMessage,
    isItemNotServicable: isSingleItemServicibilityError || isMultipleItemServicibilityError,
    isAddressNotServicable: isSelectedAddressNotServiceableError,
    isNotServicable: checkIfHasServiceabilityError(errorResponses) || shippingHandles.length === 0,
    isAddItemsWorthDelivery: isAddItemsWorthDeliveryError,
    isWeightBasedShipping: isWeightBasedShippingError,
    triggerCheckoutErrorInfoToast,
  };
}

export const getErrorFlags = (errorResponses: CheckoutErrorResponse[] | undefined) => {
  const defaultFlags = {
    isAddItemsWorthDeliveryError: false,
    isWeightBasedShippingError: false,
    isSingleItemServicibilityError: false,
    isMultipleItemServicibilityError: false,
    isSelectedAddressNotServiceableError: false,
  };
  if (!errorResponses) return defaultFlags;
  return errorResponses.reduce((flags, error) => {
    switch (error.code) {
      case CheckoutErrorResponseCode.UARC_006:
        flags.isAddItemsWorthDeliveryError = true;
        break;
      case CheckoutErrorResponseCode.UARC_007:
        flags.isWeightBasedShippingError = true;
        break;
      case CheckoutErrorResponseCode.UARC_008:
        flags.isSingleItemServicibilityError = true;
        break;
      case CheckoutErrorResponseCode.UARC_009:
        flags.isMultipleItemServicibilityError = true;
        break;
      case CheckoutErrorResponseCode.UARC_010:
      case CheckoutErrorResponseCode.UARC_005:
        flags.isSelectedAddressNotServiceableError = true;
        break;
    }
    return flags;
  }, defaultFlags);
};

export const getServiceabilityErrorMessage = (
  errorResponses: CheckoutErrorResponse[] | undefined,
  { user, count }: { user?: UserType; count?: number },
) => {
  if (!errorResponses) return "";
  const {
    isAddItemsWorthDeliveryError,
    isWeightBasedShippingError,
    isSingleItemServicibilityError,
    isMultipleItemServicibilityError,
    isSelectedAddressNotServiceableError,
  } = getErrorFlags(errorResponses);
  if (isSingleItemServicibilityError || isMultipleItemServicibilityError) {
    return i18n.t(count === 1 ? "item_unservicable_on_pincode_one" : "item_unservicable_on_pincode_other", {
      count: count,
      pincode: user?.default_shipping_address?.zip,
    });
  } else if (isAddItemsWorthDeliveryError) {
    return errorResponses.find((error) => error.code === CheckoutErrorResponseCode.UARC_006)?.message;
  } else if (isWeightBasedShippingError) {
    return errorResponses.find((error) => error.code === CheckoutErrorResponseCode.UARC_007)?.message;
  } else if (isSelectedAddressNotServiceableError) {
    const errorMessage005 = errorResponses.find(
      (error) => error.code === CheckoutErrorResponseCode.UARC_005,
    )?.message;
    const errorMessage010 = errorResponses.find(
      (error) => error.code === CheckoutErrorResponseCode.UARC_010,
    )?.message;
    return errorMessage005 || errorMessage010;
  }
};

export const checkIfHasServiceabilityError = (errorResponses: CheckoutErrorResponse[] | undefined) => {
  if (!errorResponses) return false;
  const errorCodes = [
    CheckoutErrorResponseCode.UARC_006,
    CheckoutErrorResponseCode.UARC_007,
    CheckoutErrorResponseCode.UARC_008,
    CheckoutErrorResponseCode.UARC_009,
    CheckoutErrorResponseCode.UARC_010,
    CheckoutErrorResponseCode.UARC_005,
  ];
  return errorResponses.some((error) => errorCodes.includes(error.code));
};
